import { useCallback, useEffect, useRef, useState } from "react";
import { Icone } from "@/components/Icone";
import { api, tokenAtual } from "../api";

type Balao =
  | { tipo: "cliente"; texto: string }
  | { tipo: "agente"; texto: string }
  | { tipo: "ferramenta"; nome: string; entrada: unknown; saida: string }
  | { tipo: "erro"; texto: string };

interface Estado {
  agenda: { id: string; quando: string; titulo: string; profissional: string }[];
  fichas: {
    telefone: string;
    nome: string | null;
    interesse: string | null;
    etapa: string;
    observacoes: string | null;
  }[];
}

const TELEFONE = "5511988887777";


export function Bancada({ slug, nome }: { slug: string; nome: string }) {
  const [baloes, setBaloes] = useState<Balao[]>([]);
  const [rascunho, setRascunho] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [estado, setEstado] = useState<Estado | null>(null);
  const fim = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const tenant = slug;

  const carregarEstado = useCallback(async () => {
    if (!tenant) return;
    setEstado(await api<Estado>(`/bancada/estado?tenant=${tenant}`).catch(() => null));
  }, [tenant]);

  useEffect(() => {
    void carregarEstado();
  }, [carregarEstado]);

  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: "smooth" });
  }, [baloes]);

  async function enviar() {
    const texto = rascunho.trim();
    if (!texto || ocupado || !tenant) return;

    setRascunho("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    setBaloes((b) => [...b, { tipo: "cliente", texto }]);
    setOcupado(true);

    try {
      // Chamada crua, e não `api()`: a resposta vem em FLUXO, uma linha por
      // evento, enquanto o modelo escreve.
      const token = await tokenAtual();
      const resposta = await fetch("/api/painel/bancada", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ tenantSlug: tenant, telefone: TELEFONE, mensagem: texto }),
      });

      // Recusa vem inteira, em JSON — conversa assumida por uma pessoa, por
      // exemplo. Lida como fluxo, ela sumia sem dizer nada.
      if (!resposta.ok) {
        const falha = (await resposta.json().catch(() => null)) as { erro?: string } | null;
        throw new Error(falha?.erro ?? `O servidor respondeu ${resposta.status}.`);
      }
      if (!resposta.body) throw new Error("Sem resposta do servidor.");

      const leitor = resposta.body.getReader();
      const decodificador = new TextDecoder();
      let sobra = "";
      // Estes dois vivem FORA do updater do setState de propósito. O updater
      // precisa ser puro: em modo estrito o React o executa duas vezes, e
      // qualquer mutação aqui dentro faz a segunda passada tomar o caminho
      // errado e descartar a mensagem.
      let respondendo = false;
      let acumulado = "";

      for (;;) {
        const { done, value } = await leitor.read();
        if (done) break;
        sobra += decodificador.decode(value, { stream: true });
        const linhas = sobra.split("\n");
        sobra = linhas.pop() ?? "";

        for (const linha of linhas) {
          if (!linha.trim()) continue;
          const evento = JSON.parse(linha);

          if (evento.tipo === "texto") {
            acumulado += evento.delta as string;
            const textoAtual = acumulado;
            if (!respondendo) {
              respondendo = true;
              setBaloes((b) => [...b, { tipo: "agente", texto: textoAtual }]);
            } else {
              setBaloes((b) => {
                const copia = [...b];
                const ultimo = copia[copia.length - 1];
                if (ultimo?.tipo === "agente") {
                  copia[copia.length - 1] = { ...ultimo, texto: textoAtual };
                }
                return copia;
              });
            }
          } else if (evento.tipo === "ferramenta") {
            respondendo = false;
            acumulado = "";
            setBaloes((b) => [
              ...b,
              { tipo: "ferramenta", nome: evento.nome, entrada: evento.entrada, saida: evento.saida },
            ]);
          } else if (evento.tipo === "erro") {
            setBaloes((b) => [...b, { tipo: "erro", texto: evento.mensagem }]);
          }
        }
      }
    } catch (e) {
      setBaloes((b) => [...b, { tipo: "erro", texto: e instanceof Error ? e.message : "Falhou." }]);
    } finally {
      setOcupado(false);
      void carregarEstado();
    }
  }

  async function reiniciar() {
    if (!tenant) return;
    await api("/bancada", { method: "POST", body: JSON.stringify({ tenantSlug: tenant, telefone: TELEFONE, reiniciar: true }) }).catch(() => null);
    setBaloes([]);
    void carregarEstado();
  }

  return (
    <div className="flex h-full min-h-0">
      <section className="bg-chat flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 shrink-0 flex-wrap items-center gap-3 border-b border-[var(--color-borda)] bg-[var(--color-cartao)] px-4 py-2.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-realce)] text-[var(--color-marca-forte)]">
            <Icone nome="bancada" className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-[15.5px] leading-tight font-bold">Bancada do assistente</h1>
            {/* Antes era silencioso — testava sempre "aurora", trocasse o dono de
                clínica ou não. Agora mostra qual, pra nunca mais ser preciso adivinhar. */}
            <p className="text-[12.5px] text-[var(--color-suave)]">
              Testando <b className="font-semibold text-[var(--color-tinta)]">{nome}</b> · a mesma rota do WhatsApp, sem WhatsApp e sem custo de mensagem
            </p>
          </div>
          <button
            type="button"
            onClick={reiniciar}
            className="flex h-9 items-center gap-1.5 rounded-full bg-[var(--color-cartao)] px-4 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao hover:bg-[var(--color-painel)]"
          >
            <Icone nome="reabrir" className="h-4 w-4" />
            Recomeçar
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto py-4">
          <div className="mx-auto flex max-w-[880px] flex-col gap-2 px-3 md:px-5">
            {baloes.length === 0 && (
              <div className="my-10 flex justify-center">
                <p className="max-w-sm rounded-[14px] bg-[var(--color-cartao)] px-4 py-3 text-center text-sm text-[var(--color-suave)] shadow-cartao">
                  Escreva como um paciente escreveria. Ex.: “oi, vcs fazem limpeza de pele?”
                </p>
              </div>
            )}

            {baloes.map((b, i) => {
              if (b.tipo === "ferramenta") {
                return (
                  <details key={i} className="group self-start">
                    <summary className="flex h-7 cursor-pointer list-none items-center gap-1.5 rounded-full bg-[var(--color-painel)] px-3 text-xs text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)] [&::-webkit-details-marker]:hidden">
                      <Icone nome="ajustes" className="h-3.5 w-3.5" />
                      usou <span className="font-mono">{b.nome}</span>
                    </summary>
                    <pre className="mt-1.5 max-w-[min(680px,90vw)] overflow-x-auto rounded-xl bg-[var(--color-cartao)] p-3 text-[11.5px] break-words whitespace-pre-wrap text-[var(--color-suave)] shadow-cartao">
{JSON.stringify(b.entrada, null, 2)}
{"\n→ "}{b.saida}
                    </pre>
                  </details>
                );
              }
              if (b.tipo === "erro") {
                return (
                  <p key={i} className="self-center rounded-[14px] bg-[var(--color-atencao-fundo)] px-3.5 py-2 text-sm text-[var(--color-atencao)]">
                    {b.texto}
                  </p>
                );
              }
              // Aqui quem escreve é você, no papel do paciente: a sua bolha fica à direita.
              const meu = b.tipo === "cliente";
              return (
                <div key={i} className={meu ? "flex justify-end" : "flex justify-start"}>
                  <div
                    className={
                      "max-w-[84%] rounded-[14px] px-3 pt-2 pb-2 text-[14.5px] leading-[1.45] whitespace-pre-wrap shadow-[0_1px_1px_rgba(0,0,0,0.05)] md:max-w-[min(68%,560px)] dark:shadow-none " +
                      (meu
                        ? "rounded-tr-[4px] bg-[var(--color-cliente)] text-[var(--color-tinta)]"
                        : "rounded-tl-[4px] bg-[var(--color-agente)] text-[var(--color-agente-tinta)]")
                    }
                  >
                    {!meu && (
                      <p className="mb-0.5 flex items-center gap-1 text-xs font-bold text-[var(--color-marca-forte)]">
                        <Icone nome="agente" className="h-3 w-3" />
                        Assistente
                      </p>
                    )}
                    {meu && <p className="mb-0.5 text-xs font-bold text-[var(--color-fraco)]">Você, como paciente</p>}
                    {b.texto}
                  </div>
                </div>
              );
            })}
            {ocupado && baloes.at(-1)?.tipo === "cliente" && (
              <div className="flex gap-1 self-start rounded-[14px] rounded-tl-[4px] bg-[var(--color-agente)] px-3.5 py-3" aria-label="O assistente está escrevendo">
                {[0, 1, 2].map((n) => (
                  <span key={n} className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--color-marca-forte)]" style={{ animationDelay: `${n * 180}ms` }} />
                ))}
              </div>
            )}
            <div ref={fim} />
          </div>
        </div>

        <footer className="shrink-0 px-2.5 pt-2 pb-2.5 md:px-4 md:pb-3.5">
          <div className="mx-auto flex max-w-[880px] items-end gap-2">
            <div className="flex min-w-0 flex-1 items-end rounded-[24px] bg-[var(--color-cartao)] px-3 py-1 shadow-cartao focus-within:shadow-[0_0_0_1.5px_var(--color-marca)]">
              <textarea
                ref={textareaRef}
                rows={1}
                value={rascunho}
                onChange={(e) => {
                  setRascunho(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void enviar();
                  }
                }}
                aria-label="Escreva como paciente"
                placeholder="Escreva como paciente"
                className="max-h-[140px] min-h-10 min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[14.5px] leading-[1.45] text-[var(--color-tinta)] outline-none placeholder:text-[var(--color-fraco)]"
              />
            </div>
            <button
              type="button"
              onClick={enviar}
              disabled={ocupado || !rascunho.trim() || !tenant}
              aria-label="Enviar"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-marca)] text-[var(--color-sobre-marca)] transition-[filter,transform] hover:brightness-110 active:scale-95 disabled:opacity-40"
            >
              <Icone nome="enviar" className="h-[18px] w-[18px]" />
            </button>
          </div>
        </footer>
      </section>

      {/* O que a conversa produziu, no lugar da ficha do paciente. */}
      <aside className="hidden w-[320px] shrink-0 overflow-y-auto border-l border-[var(--color-borda)] bg-[var(--color-cartao)] lg:block" aria-label="O que a conversa produziu">
        <Painel titulo="Agenda">
          {estado?.agenda.length ? (
            estado.agenda.map((e) => (
              <div key={e.id} className="flex flex-col gap-0.5 rounded-xl bg-[var(--color-painel)] px-3 py-2.5 text-[13px]">
                <p className="font-bold">{e.quando}</p>
                <p className="text-[var(--color-suave)]">{e.titulo}</p>
                <p className="text-[var(--color-fraco)]">{e.profissional}</p>
              </div>
            ))
          ) : (
            <Vazio>Nada marcado ainda.</Vazio>
          )}
        </Painel>

        <Painel titulo="Fichas">
          {estado?.fichas.length ? (
            estado.fichas.map((f) => (
              <div key={f.telefone} className="flex flex-col gap-0.5 rounded-xl bg-[var(--color-painel)] px-3 py-2.5 text-[13px]">
                <p className="font-bold">{f.nome ?? "sem nome ainda"}</p>
                <p className="text-[var(--color-suave)]">
                  {f.interesse ?? "interesse não anotado"} · {f.etapa}
                </p>
                {f.observacoes && <p className="text-[var(--color-fraco)]">{f.observacoes}</p>}
              </div>
            ))
          ) : (
            <Vazio>Nenhuma ficha ainda.</Vazio>
          )}
        </Painel>
      </aside>
    </div>
  );
}

function Painel({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2 border-b border-[var(--color-borda)] px-5 py-4 last:border-0">
      <h2 className="rotulo">{titulo}</h2>
      {children}
    </section>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return <p className="text-[13.5px] text-[var(--color-suave)]">{children}</p>;
}
